import React, { useState } from "react";
import { fetchJson } from "../../lib/api";
import type { BankAccount } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Input } from "../input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";

export const AccountManagerModal = ({
  houseId,
  accounts,
  onClose,
  onRefresh,
  isReadonly,
}: {
  houseId: number;
  accounts: BankAccount[];
  onClose: () => void;
  onRefresh: () => void;
  isReadonly: boolean;
}) => {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [currency, setCurrency] = useState("DKK");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const payload = {
      name: name.trim(),
      number: number.trim(),
      currency: currency.trim().toUpperCase(),
    };
    if (!payload.name || !payload.number || !payload.currency) {
      setError(t("bankAccounts.required", "Name, account number, and currency are required."));
      return;
    }
    setIsSubmitting(true);
    try {
      await fetchJson(`/api/houses/${houseId}/bank-accounts`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      setName("");
      setNumber("");
      setCurrency("DKK");
      await onRefresh();
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : t("bankAccounts.saveFailed", "Failed to save account"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const remove = async (accountId: number) => {
    setError(null);
    if (!window.confirm(t("bankAccounts.confirmDelete", "Delete this account? This cannot be undone."))) {
      return;
    }
    setRemovingId(accountId);
    try {
      await fetchJson(`/api/houses/${houseId}/bank-accounts/${accountId}`, { method: "DELETE" });
      await onRefresh();
    } catch (removeError) {
      setError(
        removeError instanceof Error
          ? removeError.message
          : t("bankAccounts.removeFailed", "Failed to remove account"),
      );
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-xl">
        <CardHeader className="relative">
          <CardTitle>{t("bankAccounts.manageTitle", "Manage bank accounts")}</CardTitle>
          <p className="text-sm text-muted">
            {t("bankAccounts.manageSubtitle", "Add or remove bank accounts for this house.")}
          </p>
          <button
            type="button"
            className="absolute right-4 top-4 text-xl text-muted transition hover:text-ink"
            onClick={onClose}
            aria-label={t("common.close", "Close")}
          >
            ×
          </button>
        </CardHeader>
        <CardContent className="space-y-4">
          {isReadonly ? (
            <p className="rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t(
                "bankAccounts.readonly",
                "This house is read-only. Bank accounts can only be managed by full access users.",
              )}
            </p>
          ) : null}
          <form className="space-y-3" onSubmit={submit}>
            <div className="grid gap-3 md:grid-cols-3">
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t("bankAccounts.namePlaceholder", "Account name")}
                required
                disabled={isReadonly}
              />
              <Input
                value={number}
                onChange={(event) => setNumber(event.target.value)}
                placeholder={t("bankAccounts.numberPlaceholder", "Account number")}
                required
                disabled={isReadonly}
              />
              <Input
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
                placeholder={t("bankAccounts.currencyPlaceholder", "Currency")}
                required
                disabled={isReadonly}
              />
            </div>
            {error ? <p className="text-sm text-error">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <Button type="submit" disabled={isSubmitting || isReadonly}>
                {isSubmitting
                  ? t("common.saving", "Saving...")
                  : t("bankAccounts.add", "Add bank account")}
              </Button>
            </div>
          </form>

          <div className="space-y-2">
            {accounts.length === 0 ? (
              <p className="text-sm text-muted">{t("bankAccounts.empty", "No accounts yet.")}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("bankAccounts.name", "Name")}</TableHead>
                    <TableHead>{t("bankAccounts.number", "Number")}</TableHead>
                    <TableHead>{t("bankAccounts.currency", "Currency")}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>{account.name}</TableCell>
                      <TableCell>{account.number}</TableCell>
                      <TableCell>{account.currency}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => remove(account.id)}
                          disabled={removingId === account.id || isReadonly}
                        >
                          {removingId === account.id
                            ? t("common.removing", "Removing...")
                            : t("common.remove", "Remove")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
